import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown, { DropdownOption } from '../../shared/TailwindComponents/Dropdown';
import Textarea from '../../shared/TailwindComponents/Textarea';
import CurrencyPicker from '../../shared/TailwindComponents/CurrencyPicker';
import CurrencyInput from '../../shared/TailwindComponents/CurrencyInput';
import type { RateOverride } from '../../../types/economics';

export interface RateOverridePayload {
  /** uuid-first write (see MATTER_ECONOMICS_API.md). */
  user_uuid: string;
  rate: number;
  currency: string;
  notes?: string;
}

interface EditRateOverrideModalProps {
  /** Existing override when editing; null/undefined when adding. */
  rateOverride?: RateOverride | null;
  /** Picker options limited to the matter's collaborators (value = user_uuid). */
  collaborators: DropdownOption[];
  /** Matter currency, used as the default. */
  defaultCurrency: string;
  onSave: (payload: RateOverridePayload) => Promise<void>;
  onAfterSave?: () => Promise<void> | void;
  /** Reports whether a collaborator (user_uuid) is selected, so the host can disable Save. */
  onValidityChange?: (valid: boolean) => void;
}

export interface EditRateOverrideModalHandle {
  handleSave: () => Promise<void>;
  isSaving: boolean;
}

/**
 * Add / edit a per-matter rate override (US #2236, task #2257). Rendered via
 * GlobalModal ('editRateOverride'). The user picker is locked when editing.
 */
const EditRateOverrideModal = forwardRef<EditRateOverrideModalHandle, EditRateOverrideModalProps>(
  ({ rateOverride, collaborators, defaultCurrency, onSave, onAfterSave, onValidityChange }, ref) => {
    const { t } = useTranslation();
    const isEditing = !!rateOverride;

    // Binds to user_uuid (the picker option value), NOT the numeric user_id.
    const [userUuid, setUserUuid] = useState<string>('');
    const [rate, setRate] = useState<string>('');
    const [currency, setCurrency] = useState<string>(defaultCurrency);
    const [notes, setNotes] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<{ user_id?: string; rate?: string }>({});

    useEffect(() => {
      if (rateOverride) {
        // Editing: identity is fixed and not reassigned (PATCH updates rate/notes
        // only). The host passes the single locked option, so adopt its value.
        setUserUuid(collaborators[0]?.value ? String(collaborators[0].value) : '');
        setRate(String(rateOverride.rate ?? ''));
        setCurrency(rateOverride.currency || defaultCurrency);
        setNotes(rateOverride.notes || '');
      }
    }, [rateOverride, defaultCurrency, collaborators]);

    // A collaborator (user_uuid) must be chosen before the host enables Save.
    const isValid = isEditing || !!userUuid;
    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    const validate = (): boolean => {
      const next: { user_id?: string; rate?: string } = {};
      if (!isEditing && !userUuid) next.user_id = 'Select a collaborator';
      const rateNum = Number(rate);
      if (rate.trim() === '') next.rate = 'Rate is required';
      else if (Number.isNaN(rateNum)) next.rate = 'Enter a valid amount';
      else if (rateNum <= 0) next.rate = 'Rate must be greater than 0';
      setErrors(next);
      return Object.keys(next).length === 0;
    };

    const handleSave = async () => {
      if (!validate()) {
        // Throw so GlobalModal keeps the dialog open and the errors stay visible.
        throw new Error('validation');
      }
      setIsSaving(true);
      try {
        await onSave({
          user_uuid: userUuid,
          rate: Number(rate),
          currency,
          notes: notes.trim() || undefined,
        });
        if (onAfterSave) await onAfterSave();
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({ handleSave, isSaving }));

    return (
      <div className="px-6 space-y-5 w-[34rem] max-w-[90vw]">
        <div>
          <Dropdown
            id="rate-override-user"
            label={t('Collaborator')}
            options={collaborators}
            value={userUuid}
            placeholder={t('Select a collaborator')}
            onChange={(v) => setUserUuid(Array.isArray(v) ? v[0] : v)}
            error={errors.user_id ? t(errors.user_id) : undefined}
            required
            // Identity is fixed once an override exists; edit changes the rate.
            disableParentSelection={isEditing}
            fullWidth
            className="w-full"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <CurrencyInput
            id="rate-override-rate"
            label={t('Rate')}
            value={rate}
            currency={currency}
            onChange={setRate}
            error={errors.rate ? t(errors.rate) : undefined}
            required
          />
          <CurrencyPicker
            id="rate-override-currency"
            label={t('Currency')}
            value={currency}
            onChange={setCurrency}
          />
        </div>

        <Textarea
          id="rate-override-notes"
          label={t('Notes')}
          value={notes}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
          placeholder={t('Optional note (e.g. senior rate for this matter)')}
          rows={3}
        />
      </div>
    );
  },
);

EditRateOverrideModal.displayName = 'EditRateOverrideModal';

export default EditRateOverrideModal;
